import { useCallback } from 'react';
import { useCanUseTeamView } from 'abilities/team';
import { findTeamMember } from 'components/Team/teamUtils';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import {
  useIsManagedUser,
  useIsTrialUser,
  useUserContext,
} from '../../context/user/UserContext';
import { useUserSubscription } from '../Account/operation.generated';
import usePromptForSubscriptionUpgrade from '../Account/usePromptForSubscriptionUpgrade';

const useUserAvatar = () => {
  const { data } = useUserContext();
  const { data: subscriptionData } = useUserSubscription();
  const isTrialUser = useIsTrialUser();
  const [, dispatch] = usePromptsContext();

  const upgradePath = 'Dashboard > Account Dropdown Menu';
  const userId = data?.user.id ?? '';
  const name = data?.user.profile.name ?? '';
  const email = data?.user.profile.email ?? '';
  const initials =
    findTeamMember(data?.user.team, userId)?.initials ?? name.substring(0, 2);
  let subscriptionDisplayName =
    subscriptionData?.user.subscription?.plan.tier ?? '';
  const canUseTeamView = useCanUseTeamView();
  const isManagedUser = useIsManagedUser();

  // Handling a case where the team subscription is expired/cancelled and the user is back to the trial tier
  if (isTrialUser && !canUseTeamView) {
    subscriptionDisplayName = 'trial';
  }

  let showUpgrade = false;
  switch (subscriptionDisplayName?.toLowerCase()) {
    case '':
    case 'trial':
    case 'maker':
    case 'creative':
    case 'business':
      if (!isManagedUser) {
        showUpgrade = true;
      }
      break;
    default:
      break;
  }

  const [openUpgradePrompt] = usePromptForSubscriptionUpgrade({
    upgradeContext: 'default',
    upgradePath,
  });

  const handleUpgrade = useCallback(() => {
    if (isManagedUser) {
      return;
    }
    if (isTrialUser) {
      dispatch({
        type: 'OpenSubscriptionTiersPrompt',
        payload: {
          upgradeContext: 'increase_access',
          upgradePath,
        },
      });
    } else {
      openUpgradePrompt();
    }
  }, [dispatch, isManagedUser, isTrialUser, openUpgradePrompt, upgradePath]);

  return {
    userId,
    initials,
    name,
    email,
    // note: avoiding relabeling to preserve backwards compatibility
    // in the future we may want to be more explicit naming wise
    subscription: subscriptionDisplayName,
    subscriptionName:
      subscriptionData?.user.subscription?.plan.displayName ?? 'trial',
    showUpgrade,
    onUpgrade: handleUpgrade,
  };
};

export default useUserAvatar;
